import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { connect } from "react-redux";
import type { AnyAction, Dispatch } from "redux";
import axios from "axios";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { axiosApiInstance } from "../../axios";
import SearchableMultiSelect from "../../components/controls/SearchableMultiSelect";
import InputTextOnly from "../../components/controls/InputTextOnly";
import { useToast } from "../../contexts/ToastContext";
import { makeGetApiCall, makePutApiCall } from "../../shared/makeApiCall";
import { companyLogoUrl } from "../../shared/companyBranding";
import * as actions from "../../store/actions";
import type { ApiErrorPayload } from "../../types/api";
import type { CompanySettingsPayload, CompanySettingsResponse, Tenant } from "../../types/auth";

type CompanyField = Extract<keyof CompanySettingsPayload, keyof Tenant>;
type CompanyFieldDefinition = readonly [CompanyField, string, boolean?];

const fields: readonly CompanyFieldDefinition[] = [
  ["name", "Company name", true],
  ["company_email", "Company email"],
  ["phone_number", "Phone number"],
  ["address_1", "Address line 1"],
  ["address_2", "Address line 2"],
  ["city", "City"],
  ["state", "Province / state"],
  ["zip", "Postal / ZIP code"],
  ["country", "Country"],
];

interface CompanySettingsProps {
  tenant: Tenant | null;
  updateTenant: (tenant: Tenant) => void;
}

interface CompanyState {
  auth: { tenant: Tenant | null };
}

const apiErrorMessage = (error: ApiErrorPayload | undefined, fallback: string) =>
  error?.message ?? Object.values(error?.errors ?? {}).flat()[0] ?? fallback;

const responseTenant = (response: CompanySettingsResponse): Tenant | null =>
  response.tenant && typeof response.tenant.name === "string" ? response.tenant : null;

export function CompanySettings({ tenant, updateTenant }: CompanySettingsProps) {
  const [form, setForm] = useState<Partial<Tenant>>(tenant ?? {});
  const [recipientIds, setRecipientIds] = useState<number[]>([]);
  const [recipientOptions, setRecipientOptions] = useState<Array<{ value: number; label: string }>>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  const { showSuccess } = useToast();

  useEffect(() => {
    makeGetApiCall<CompanySettingsResponse>("/company-settings").then((result) => {
      const loadedTenant = result.success ? responseTenant(result.data) : null;
      if (result.success && loadedTenant) {
        setSettingsLoaded(true);
        setForm(loadedTenant);
        setRecipientIds(result.data.order_notification_user_ids ?? []);
        setRecipientOptions(result.data.order_notification_user_options ?? []);
      } else setError(result.data?.message || "Could not load company settings.");
      setLoading(false);
    });
  }, []);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const payload: CompanySettingsPayload = {
      name: form.name?.trim() ?? "",
      company_email: form.company_email?.trim() || null,
      phone_number: form.phone_number?.trim() || null,
      address_1: form.address_1?.trim() || null,
      address_2: form.address_2?.trim() || null,
      city: form.city?.trim() || null,
      state: form.state?.trim() || null,
      zip: form.zip?.trim() || null,
      country: form.country?.trim() || null,
      sync_orders_to_qb_estimates: Boolean(form.sync_orders_to_qb_estimates),
      order_notification_user_ids: recipientIds,
    };
    const result = await makePutApiCall<CompanySettingsResponse, CompanySettingsPayload>(
      "/company-settings",
      payload
    );
    if (result.success) {
      const updatedTenant = responseTenant(result.data);
      if (updatedTenant) {
        setForm(updatedTenant);
        setRecipientIds(result.data.order_notification_user_ids ?? []);
        setRecipientOptions(result.data.order_notification_user_options ?? []);
        updateTenant(updatedTenant);
        showSuccess(result.data.message || "Company settings updated.");
      } else {
        setError("The server returned invalid company settings.");
      }
    } else setError(apiErrorMessage(result.data, "Could not save company settings."));
    setSaving(false);
  };

  const uploadLogo = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    const data = new FormData();
    data.append("logo", file);
    try {
      const response = await axiosApiInstance.post<CompanySettingsResponse>(
        "/company-settings/logo",
        data,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        }
      );
      const updatedTenant = responseTenant(response.data);
      if (!updatedTenant) throw new Error("Invalid company settings response");
      setForm((current) => ({ ...current, logo_url: updatedTenant.logo_url }));
      updateTenant(updatedTenant);
      showSuccess(response.data.message || "Company logo updated.");
    } catch (requestError: unknown) {
      const responseError = axios.isAxiosError<ApiErrorPayload>(requestError)
        ? requestError.response?.data
        : undefined;
      setError(apiErrorMessage(responseError, "Could not upload the logo."));
    }
    setUploading(false);
    event.target.value = "";
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading company settings…</div>;

  const logoUrl = companyLogoUrl(form);

  return (
    <form onSubmit={save} className="mx-auto max-w-5xl space-y-6">
      <div>
        {/* <p className="text-xs font-semibold uppercase tracking-widest text-main-purple">Account</p> */}
        <h1 className="mt-1 text-2xl font-bold text-gray-900">Company settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage the company identity shown throughout FluxOrders and on customer invoices.
        </p>
      </div>
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {error}
        </div>
      )}
      <section className="card-custom overflow-hidden">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="font-semibold text-gray-900">Company logo</h2>
          <p className="mt-1 text-sm text-gray-500">
            PNG, JPG, WebP, or SVG up to 2 MB. This logo appears in navigation and on generated
            invoices.
          </p>
        </div>
        <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center">
          <div className="flex h-28 w-44 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${form.name || "Company"} logo`}
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <div className="text-center text-gray-400">
                <PhotoIcon className="mx-auto h-8 w-8" />
                <span className="mt-2 block text-xs">No company logo</span>
              </div>
            )}
          </div>
          <div>
            <input
              ref={fileInput}
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={uploadLogo}
              className="hidden"
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
              className="rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {uploading ? "Uploading…" : logoUrl ? "Replace logo" : "Upload logo"}
            </button>
            <p className="mt-2 text-xs text-gray-500">
              Use a clear, horizontal or square mark with a transparent or white background.
            </p>
          </div>
        </div>
      </section>
      <section className="card-custom overflow-hidden">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="font-semibold text-gray-900">Company information</h2>
          <p className="mt-1 text-sm text-gray-500">
            These details identify your company on invoices.
          </p>
        </div>
        <div className="grid gap-5 p-5 sm:grid-cols-2">
          {fields.map(([name, label, required]) => (
            <InputTextOnly
              key={name}
              parentClass={name === "name" ? "sm:col-span-2" : undefined}
              name={name}
              label={label}
              labelOn
              required={required}
              type={name === "company_email" ? "email" : "text"}
              value={String(form[name] ?? "")}
              onChange={(event) => setForm({ ...form, [name]: event.target.value })}
            />
          ))}
        </div>
        <div className="border-t border-gray-200 px-5 py-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={Boolean(form.sync_orders_to_qb_estimates)}
              onChange={(event) =>
                setForm({ ...form, sync_orders_to_qb_estimates: event.target.checked })
              }
              className="mt-1 h-4 w-4 rounded border-gray-300 text-main-purple focus:ring-main-purple"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">
                Sync Orders to QB as Estimates Automatically
              </span>
              <span className="mt-1 block text-sm text-gray-500">
                New orders are still saved in FluxOrders. QuickBooks receives an estimate only when
                this is on and the company is already connected to QuickBooks.
              </span>
            </span>
          </label>
        </div>
        <div className="border-t border-gray-200 px-5 py-4">
          <h2 className="font-semibold text-gray-900">Order notifications</h2>
          <p className="mt-1 mb-4 text-sm text-gray-500">
            Email these users whenever a new order is placed, including imported orders. Leave the
            selection empty to turn off order notification emails.
          </p>
          <SearchableMultiSelect
            inputId="order-notification-recipients"
            label="Users to notify"
            options={recipientOptions}
            value={recipientIds}
            onChange={(values) => setRecipientIds(values.map(Number))}
            disabled={saving}
            placeholder="Select users"
            showClear
          />
        </div>
        <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-5 py-4">
          <button
            type="submit"
            disabled={saving || !settingsLoaded}
            className="rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </section>
    </form>
  );
}

export default connect(
  (state: CompanyState) => ({ tenant: state.auth.tenant }),
  (dispatch: Dispatch<AnyAction>) => ({
    updateTenant: (tenant: Tenant) => dispatch(actions.updateTenantSuccess(tenant)),
  })
)(CompanySettings);
